import { pageDataKeys } from "~/app/api/pageData";
import { occBaseSiteUrl, occFetch } from "~/app/api/occ/occFetch";
import { occCartUserPath } from "~/app/api/occ/occUserPath";
import { cancelLaneWork, laneOnIdle } from "~/app/api/requestLanes";
import { queryClient } from "~/app/queryClient";
import { pnaKeys } from "~/domains/pna/pnaKeys";

export function send(cartId: string): Promise<unknown> {
  if (!cartId) {
    return Promise.reject(new Error("Cannot delete a cart without its explicit resource identity"));
  }
  return deleteEntireCart(cartId);
}

/**
 * Deleting an entire cart supersedes all lane work (task 0045): the abort epoch rejects every
 * queued PnA/mutation task and aborts in-flight fetches, the canonical PnA queries are cancelled
 * (never removed — the account-transition lifecycle owns removal), and the lane drains before the
 * DELETE is sent so it cannot interleave with a superseded connection. Quote clearing shares
 * exactly this path.
 */
export async function deleteEntireCart(cartId: string): Promise<Response> {
  if (!cartId) {
    throw new Error("Cannot delete a cart without its explicit resource identity");
  }
  cancelLaneWork();
  await queryClient.cancelQueries({ queryKey: pnaKeys.all() });
  await laneOnIdle();
  return deleteExplicitCart(cartId);
}

export async function deleteExplicitCart(cartId: string): Promise<Response> {
  if (!cartId) {
    throw new Error("Cannot delete a cart without its explicit resource identity");
  }
  const response = await occFetch(occBaseSiteUrl(`${occCartUserPath()}/carts/${encodeURIComponent(cartId)}`), {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error("Error when clearing cart");
  }

  try {
    await queryClient.invalidateQueries({
      exact: true,
      queryKey: pageDataKeys.session(),
    });
  } catch {
    // Deletion already succeeded. Cache refresh failure must not create a retryable cart mutation.
  }
  return response;
}
